/**
 * Install handoff for shared Statbox links.
 * iOS: STATBOX1. + base64url(JSON { u, t }) copied on the store-button tap.
 * Android: Play install referrer sb=1&u=&t=.
 */

export const HANDOFF_PREFIX = "STATBOX1."
export const HANDOFF_TTL_MS = 24 * 60 * 60 * 1000
export const IOS_APP_STORE_URL =
  "https://apps.apple.com/fr/app/statbox-sports-analytics/id6748250237?l=en-GB"
export const ANDROID_PACKAGE_ID = "com.statbox.app"
export const ANDROID_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.statbox.app&pcampaignid=web_share"

const CONTENT_HOSTS = new Set(["statbox.co", "www.statbox.co", "localhost", "127.0.0.1"])
const RESERVED_JOIN = new Set(["game", "tournament", "league", "academy"])
const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/"

function encodeBase64Url(input) {
  const bytes = new TextEncoder().encode(input)
  let out = ""
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]
    const b = i + 1 < bytes.length ? bytes[i + 1] : 0
    const c = i + 2 < bytes.length ? bytes[i + 2] : 0
    const triple = (a << 16) | (b << 8) | c
    out += B64[(triple >> 18) & 63]
    out += B64[(triple >> 12) & 63]
    out += i + 1 < bytes.length ? B64[(triple >> 6) & 63] : "="
    out += i + 2 < bytes.length ? B64[triple & 63] : "="
  }
  return out.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "")
}

function normalizedPath(path) {
  const withSlash = path.startsWith("/") ? path : `/${path}`
  if (withSlash.length > 1 && withSlash.endsWith("/")) return withSlash.slice(0, -1)
  return withSlash
}

const BLOCKED_PREFIXES = [
  "/payment",
  "/pay",
  "/3ds",
  "/reset-password",
  "/reset-callback",
  "/confirm-email",
  "/auth-callback",
  "/app-download",
  "/login",
  "/signup",
  "/admin",
  "/community",
  "/academy",
  "/venue-owner",
  "/join/academy",
]

function isSupportedPath(path) {
  const lower = path.toLowerCase()
  if (BLOCKED_PREFIXES.some((prefix) => lower === prefix || lower.startsWith(`${prefix}/`))) return false

  const segments = path.split("/").filter(Boolean)
  const first = segments[0]
  const second = segments[1]

  if (path === "/create/join") return true
  if (first === "join" && second === "game" && segments[2]) return true
  if (first === "join" && second === "tournament" && segments[2]) return true
  if (first === "join" && second === "league" && segments[2]) return true
  if (first === "join" && second && !RESERVED_JOIN.has(second) && !segments[2]) return true
  if (first === "matches" && second === "happening-game" && segments[2]) return true
  if (first === "matches" && second === "court" && segments[2] && segments[3] && !segments[4]) return true
  if (first === "court" && second && segments[2] && !segments[3]) return true
  if (
    first === "matches" &&
    second === "modals" &&
    segments[2] === "venue" &&
    segments[3] === "replay-selection" &&
    segments[4] === "replay" &&
    segments[5]
  ) {
    return true
  }
  if (
    first === "matches" &&
    second === "modals" &&
    segments[2] === "venue" &&
    segments[3] === "replay-selection" &&
    segments[4] &&
    segments[4] !== "replay"
  ) {
    return true
  }
  if (first === "matches" && second === "modals" && segments[2] === "highlight" && segments[3]) return true
  if (first === "matches" && second === "modals" && segments[2] === "venue" && segments[3]) return true
  if (path === "/matches") return true
  if (first === "feed" && second === "modals" && segments[2] === "profile" && segments[3]) return true
  if (first === "profile" && second) return true
  if (path === "/profile") return true
  if (first === "feed" && second === "modals" && segments[2] === "challenge" && segments[3]) return true
  if (first === "challenge" && second) return true
  if (first === "game" && second) return true
  if (first === "match" && second) return true
  if (first === "leaderboards" || first === "leaderboard") return true
  if (first === "s" && second && !segments[2]) return true
  return false
}

/** Canonical https://www.statbox.co URL, or null when this page is not an app destination. */
export function canonicalContentUrl(raw) {
  let url
  try {
    url = new URL(raw)
  } catch {
    return null
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null
  if (!CONTENT_HOSTS.has(url.hostname.toLowerCase())) return null

  const path = normalizedPath(url.pathname || "/")
  const search = new URLSearchParams(url.search)
  if (path === "/join" && search.get("code")?.trim()) {
    const code = search.get("code").trim()
    return `https://www.statbox.co/create/join?code=${encodeURIComponent(code)}`
  }
  if (!isSupportedPath(path)) return null
  const qs = search.toString()
  return `https://www.statbox.co${path}${qs ? `?${qs}` : ""}`
}

export function isIosBrowser() {
  if (typeof navigator === "undefined") return false
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

export function isAndroidBrowser() {
  if (typeof navigator === "undefined") return false
  return /Android/i.test(navigator.userAgent)
}

export function isHandoffFresh(issuedAt, now = Date.now()) {
  return Number.isFinite(issuedAt) && issuedAt <= now + 5 * 60 * 1000 && now - issuedAt <= HANDOFF_TTL_MS
}

export function buildIosHandoffPayload(canonicalUrl, issuedAt) {
  if (!canonicalUrl || !Number.isFinite(issuedAt)) return null
  if (!canonicalContentUrl(canonicalUrl) && !canonicalUrl.startsWith("https://www.statbox.co/")) return null
  return `${HANDOFF_PREFIX}${encodeBase64Url(JSON.stringify({ u: canonicalUrl, t: issuedAt }))}`
}

export function buildAndroidReferrer(canonicalUrl, issuedAt) {
  if (!canonicalUrl || !Number.isFinite(issuedAt)) return null
  return `sb=1&u=${encodeURIComponent(canonicalUrl)}&t=${Math.trunc(issuedAt)}`
}

export function buildPlayStoreUrl(canonicalUrl, issuedAt) {
  const referrer = buildAndroidReferrer(canonicalUrl, issuedAt)
  if (!referrer) return null
  return `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_ID}&referrer=${encodeURIComponent(referrer)}`
}

function writeWithExecCommand(text) {
  try {
    const area = document.createElement("textarea")
    area.value = text
    area.setAttribute("readonly", "")
    area.style.position = "fixed"
    area.style.top = "0"
    area.style.left = "0"
    area.style.opacity = "0"
    document.body.appendChild(area)
    area.focus()
    area.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(area)
    return ok
  } catch {
    return false
  }
}

/** Must run inside the click handler, before any await, so iOS keeps the user gesture. */
export function writeClipboardFromGesture(text) {
  let copied = false
  try {
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text)
      copied = true
    }
  } catch {
    copied = false
  }
  if (!copied) copied = writeWithExecCommand(text)
  else writeWithExecCommand(text)
  return copied || writeWithExecCommand(text)
}

export function applySmartAppBanner(destination) {
  if (typeof document === "undefined") return
  const argument = destination ? `, app-argument=${destination}` : ""
  const content = `app-id=6748250237${argument}`
  let metaTag = document.querySelector('meta[name="apple-itunes-app"]')
  if (!metaTag) {
    metaTag = document.createElement("meta")
    metaTag.setAttribute("name", "apple-itunes-app")
    document.head.appendChild(metaTag)
  }
  metaTag.setAttribute("content", content)
}
